import type { SVGElement } from "highcharts";
import { renderToString } from "react-dom/server";

import { tooltipContainerConfig } from "@similarweb/pro-logic-components";

import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import noStickyTracking from "pro-features/components/Chart/src/configs/tracking/noStickyTracking";
import { translate } from "app/filters/i18nFilter";
import {
    CHART_HEIGHT,
    DEFAULT_COLOR,
    QUADRANT_BACKGROUND_IMAGE_PATH,
} from "app/pages/industry-analysis/players/table/bubble-chart/const";
import { AssetsService } from "app/services/AssetsService";
import type { IDurationObject } from "app/services/DurationService";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import type { getAverageValues } from "./chartDataUtils";
import { axisConfig } from "./chartDataUtils";
import type { ChartDataT, ChartDataPointT } from "./types";
import { KeywordGroupScatterPlotDataLabel as DataLabel } from "./KeywordGroupsScatterPlotDataLabel";
import { KeywordGroupsScatterPlotDataLabelTooltip } from "./KeywordGroupsScatterPlotDataLabelTooltip";

const ARROW_SIZE = 5;
const DEFAULT_RADIUS = 12;

interface SVGElementWithName extends SVGElement {
    name: string;
}

const connectionLines: Set<SVGElementWithName> = new Set();
const arrowHeads: Set<SVGElementWithName> = new Set();

interface Point {
    x: number;
    y: number;
}

function getDistance(point1: Point, point2: Point) {
    return Math.hypot(point2.x - point1.x, point2.y - point1.y);
}

export const getChartConfig = (
    isPeriodOverPeriod: boolean,
    chartData: ChartDataT,
    subtitleFiltersDateValue: IDurationObject<string>,
    averageValues: ReturnType<typeof getAverageValues>,
    onChartLoad: (chart: Highcharts.Chart) => void,
    radiusPreviousPeriod = DEFAULT_RADIUS,
    radiusCurrentPeriod = DEFAULT_RADIUS,
) => {
    return combineConfigs({}, [
        noStickyTracking,
        noLegendConfig,
        {
            chart: {
                type: chartTypes.SCATTER,
                height: CHART_HEIGHT.length,
                plotBackgroundImage: AssetsService.assetUrl(QUADRANT_BACKGROUND_IMAGE_PATH),
                gridLineColor: "transparent",
                events: {
                    load: function () {
                        onChartLoad(this);
                    },
                    render: function () {
                        if (connectionLines.size > 0) {
                            connectionLines.forEach((connectionLine) => {
                                connectionLine.destroy();
                            });
                            connectionLines.clear();
                        }
                        if (arrowHeads.size > 0) {
                            arrowHeads.forEach((arrowHead) => {
                                arrowHead.destroy();
                            });
                            arrowHeads.clear();
                        }

                        this.series.forEach((item) => {
                            const [currentPeriodPoint, previousPeriodPoint] = item.data;
                            const { color } = currentPeriodPoint;

                            if (
                                previousPeriodPoint &&
                                previousPeriodPoint.visible &&
                                currentPeriodPoint &&
                                currentPeriodPoint.visible
                            ) {
                                const center1: Point = {
                                    x: previousPeriodPoint.plotX + item.group.translateX,
                                    y: previousPeriodPoint.plotY + item.group.translateY + radiusPreviousPeriod / 2,
                                };
                                const center2: Point = {
                                    x: currentPeriodPoint.plotX + item.group.translateX,
                                    y: currentPeriodPoint.plotY + item.group.translateY + radiusCurrentPeriod / 2,
                                };
                                const angle = Math.atan2(center2.y - center1.y, center2.x - center1.x);
                                const distance = getDistance(center1, center2);
                                const sumOfRadii = radiusPreviousPeriod + radiusCurrentPeriod;

                                if (distance < sumOfRadii) {
                                    return;
                                }

                                const intersectionsPoint1: Point = {
                                    x: center1.x + Math.cos(angle) * radiusPreviousPeriod,
                                    y: center1.y + Math.sin(angle) * radiusPreviousPeriod,
                                };
                                const intersectionPoint2: Point = {
                                    x: center2.x - Math.cos(angle) * radiusCurrentPeriod,
                                    y: center2.y - Math.sin(angle) * radiusCurrentPeriod,
                                };

                                const connectionLine = this.renderer
                                    .path([
                                        "M",
                                        intersectionsPoint1.x,
                                        intersectionsPoint1.y,
                                        "L",
                                        intersectionPoint2.x,
                                        intersectionPoint2.y,
                                    ])
                                    .attr({
                                        stroke: color ?? DEFAULT_COLOR,
                                        strokeWidth: 1,
                                    })
                                    .add();
                                connectionLine.name = item.name;

                                const vertex1: Point = {
                                    x: intersectionPoint2.x - ARROW_SIZE * Math.cos(angle - Math.PI / 6),
                                    y: intersectionPoint2.y - ARROW_SIZE * Math.sin(angle - Math.PI / 6),
                                };
                                const vertex2: Point = {
                                    x: intersectionPoint2.x - ARROW_SIZE * Math.cos(angle + Math.PI / 6),
                                    y: intersectionPoint2.y - ARROW_SIZE * Math.sin(angle + Math.PI / 6),
                                };

                                const arrowHead = this.renderer
                                    .path([
                                        "M",
                                        intersectionPoint2.x,
                                        intersectionPoint2.y,
                                        "L",
                                        vertex1.x,
                                        vertex1.y,
                                        "L",
                                        vertex2.x,
                                        vertex2.y,
                                        "Z",
                                    ])
                                    .attr({
                                        stroke: color,
                                        fill: color,
                                        zIndex: 5,
                                    })
                                    .add();
                                arrowHead.name = item.name;

                                connectionLines.add(connectionLine);
                                arrowHeads.add(arrowHead);
                            }
                        });
                    },
                },
            },
            plotOptions: {
                series: {
                    states: {
                        inactive: {
                            enabled: true,
                            opacity: 0.4,
                        },
                        hover: {
                            enabled: false,
                        },
                    },
                },
                scatter: {
                    marker: {
                        enabled: false,
                    },
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        formatter: function () {
                            const { isPreviousPeriod, color } = this.point.options;
                            return renderToString(
                                <DataLabel
                                    isPreviousPeriod={isPreviousPeriod}
                                    color={color}
                                    radius={isPreviousPeriod ? radiusPreviousPeriod : radiusCurrentPeriod}
                                />,
                            );
                        },
                    },
                    point: {
                        events: {
                            mouseOver: function () {
                                connectionLines.forEach(
                                    (line) => line.name !== this.series.name && line.attr({ opacity: 0.4 }),
                                );
                                arrowHeads.forEach(
                                    (arrowHead) =>
                                        arrowHead.name !== this.series.name && arrowHead.attr({ opacity: 0.4 }),
                                );
                            },
                            mouseOut: function () {
                                connectionLines.forEach((line) => line.attr({ opacity: 1 }));
                                arrowHeads.forEach((arrowHead) => arrowHead.attr({ opacity: 1 }));
                            },
                        },
                    },
                },
            },
            xAxis: {
                title: {
                    text: translate(
                        "demand.analysis.search.trends.keywords.breakdown.scatterPlot.tooltip.xAxis.metric",
                    ),
                },
                lineColor: "transparent",
                gridLineColor: "transparent",
                labels: {
                    formatter: function () {
                        return axisConfig.xAxis.formatter(this.value);
                    },
                },
            },
            yAxis: {
                title: {
                    text: translate(
                        "demand.analysis.search.trends.keywords.breakdown.scatterPlot.tooltip.yAxis.metric",
                    ),
                },
                lineColor: "transparent",
                gridLineColor: "transparent",
                labels: {
                    formatter: function () {
                        return axisConfig.yAxis.formatter(this.value);
                    },
                },
            },
            tooltip: {
                ...tooltipContainerConfig().tooltip,
                padding: 0,
                useHTML: true,
                outside: true,
                split: true,
                formatter: function () {
                    const point = this.point;
                    if (!point) {return null;}

                    const data = point.options as ChartDataPointT;

                    return renderToString(
                        <KeywordGroupsScatterPlotDataLabelTooltip
                            isPeriodOverPeriod={isPeriodOverPeriod}
                            title={data.name}
                            data={data}
                            chartData={chartData}
                            averageValues={averageValues}
                            subtitleFiltersDateValue={subtitleFiltersDateValue}
                        />,
                    );
                },
            },
            series: chartData,
        },
    ]);
};
